import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useReview } from '../../context/ReviewContext';
import { useChat } from '../../context/ChatContext';
import { chatModel } from '../../data/chatModel';
import ProductReviewList from '../Review/ProductReviewList';
import ProductReviewForm from '../Review/ProductReviewForm';
import ChatWindow from '../Chat/ChatWindow';
import PaymentMethodsDisplay from './PaymentMethodsDisplay';
import GuaranteeBadge from './GuaranteeBadge';
import QuickChatQuestions from './QuickChatQuestions';
import VideoCallStatusBadge from './VideoCallStatusBadge';
import VideoCallUsageBadge from './VideoCallUsageBadge';
import RequestVideoCallModal from './RequestVideoCallModal';
import { VideoCallIcon } from '../Layout/ProfileMenuDropdown';
import LevelBadge from './LevelBadge';
import VerificationBadge, { RiskScoreBadge } from './VerificationBadge';

// Product Image Gallery Component
const ProductImageGallery = ({ images, productName }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="w-full h-96 rounded-lg overflow-hidden bg-gray-100">
        <img
          src={images[selectedIndex]}
          alt={`${productName} - Image ${selectedIndex + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                selectedIndex === index
                  ? 'border-green-500 ring-2 ring-green-200'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <img
                src={image}
                alt={`${productName} - Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProductDetail = () => {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { addToCart } = useMarketplace();
  const { canReviewProduct, getProductReviewStats } = useReview();
  const { findOrCreateChat, sendMessage } = useChat();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [reviewStats, setReviewStats] = useState({ averageRating: 0, totalReviews: 0 });
  const [isFavorite, setIsFavorite] = useState(false);
  const [remainingQuantity, setRemainingQuantity] = useState(0);
  const [showPaymentShipping, setShowPaymentShipping] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [guarantees, setGuarantees] = useState([]);
  const [storePackage, setStorePackage] = useState(null);
  const [showRequestVideoCallModal, setShowRequestVideoCallModal] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (product?.storeId) {
      loadGuarantees();
      loadStorePackage();
    }
  }, [product?.storeId]);

  const loadGuarantees = async () => {
    if (!product?.storeId) return;
    
    try {
      const guaranteeRef = doc(db, 'productGuarantees', product.storeId);
      const guaranteeSnap = await getDoc(guaranteeRef);
      
      if (guaranteeSnap.exists()) {
        const data = guaranteeSnap.data();
        // Check if this product has specific guarantees or use store defaults
        const productGuarantees = data.products?.[productId] || data.guarantees || [];
        setGuarantees(productGuarantees.filter(g => g.isActive !== false));
      }
    } catch (error) {
      console.error('Error loading guarantees:', error);
    }
  };

  const loadStorePackage = async () => {
    if (!product?.storeId) return;
    
    try {
      const packageRef = doc(db, 'storePackages', product.storeId);
      const packageSnap = await getDoc(packageRef);
      
      if (packageSnap.exists()) {
        const data = packageSnap.data();
        if (data.status === 'active') {
          setStorePackage(data.packageId || 'free');
        }
      }
    } catch (error) {
      console.error('Error loading store package:', error);
    }
  };

  useEffect(() => {
    checkCanReview();
    loadReviewStats();
    checkFavorite();
    loadRemainingQuantity();
    checkCanEdit();
  }, [product, currentUser, productId]);

  const loadRemainingQuantity = async () => {
    if (!productId || !product) return;
    
    try {
      // ดึง orders ที่มี productId นี้และ status ไม่ใช่ cancelled
      const ordersRef = collection(db, 'orders');
      const q = query(
        ordersRef,
        where('items', 'array-contains-any', [{ productId }])
      );
      
      // Fallback: ดึงทั้งหมดแล้วกรอง
      const allOrdersSnapshot = await getDocs(collection(db, 'orders'));
      let soldQuantity = 0;
      
      allOrdersSnapshot.docs.forEach(doc => {
        const orderData = doc.data();
        // ตรวจสอบว่า order มี productId นี้และ status ไม่ใช่ cancelled
        if (orderData.status !== 'cancelled' && orderData.items) {
          orderData.items.forEach(item => {
            if (item.productId === productId) {
              soldQuantity += item.quantity || 0;
            }
          });
        }
      });
      
      const remaining = Math.max(0, (product.quantity || 0) - soldQuantity);
      setRemainingQuantity(remaining);
    } catch (error) {
      console.error('Error loading remaining quantity:', error);
      // Fallback: ใช้ quantity จาก product
      setRemainingQuantity(product.quantity || 0);
    }
  };

  const checkCanEdit = async () => {
    if (!product || !currentUser || !store) {
      setCanEdit(false);
      return;
    }
    
    // ตรวจสอบว่าเป็นเจ้าของร้านหรือไม่
    if (currentUser.uid !== store.ownerId) {
      setCanEdit(false);
      return;
    }
    
    // Logic: แก้ไขได้ถ้า:
    // 1. ยังไม่มี order ที่ status != 'cancelled'
    // 2. ยังไม่มีคนหยิบลงตะกร้า (ตรวจสอบยาก - ข้ามไปก่อน)
    // 3. ยังไม่เปิดประมูล
    
    try {
      // ตรวจสอบว่ามี order ที่ไม่ใช่ cancelled หรือไม่
      const allOrdersSnapshot = await getDocs(collection(db, 'orders'));
      let hasActiveOrder = false;
      
      allOrdersSnapshot.docs.forEach(doc => {
        const orderData = doc.data();
        if (orderData.status !== 'cancelled' && orderData.items) {
          const hasThisProduct = orderData.items.some(item => item.productId === productId);
          if (hasThisProduct) {
            hasActiveOrder = true;
          }
        }
      });
      
      // ตรวจสอบว่ามีประมูลหรือไม่
      const auctionsRef = collection(db, 'auctions');
      const auctionsSnapshot = await getDocs(auctionsRef);
      let hasAuction = false;
      
      auctionsSnapshot.docs.forEach(doc => {
        const auctionData = doc.data();
        if (auctionData.productId === productId && auctionData.status === 'active') {
          hasAuction = true;
        }
      });
      
      // แก้ไขได้ถ้าไม่มี order และไม่มีประมูล
      setCanEdit(!hasActiveOrder && !hasAuction);
    } catch (error) {
      console.error('Error checking can edit:', error);
      setCanEdit(false);
    }
  };

  const loadReviewStats = async () => {
    if (!productId) return;
    try {
      const stats = await getProductReviewStats(productId);
      setReviewStats(stats);
    } catch (error) {
      console.error('Error loading review stats:', error);
    }
  };

  const checkFavorite = () => {
    if (!currentUser || !productId) {
      setIsFavorite(false);
      return;
    }
    // Check localStorage for favorites
    const favorites = JSON.parse(localStorage.getItem(`favorites_${currentUser.uid}`) || '[]');
    setIsFavorite(favorites.includes(productId));
  };

  const handleToggleFavorite = () => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบ');
      navigate('/login');
      return;
    }

    const favorites = JSON.parse(localStorage.getItem(`favorites_${currentUser.uid}`) || '[]');
    if (isFavorite) {
      const newFavorites = favorites.filter(id => id !== productId);
      localStorage.setItem(`favorites_${currentUser.uid}`, JSON.stringify(newFavorites));
      setIsFavorite(false);
    } else {
      favorites.push(productId);
      localStorage.setItem(`favorites_${currentUser.uid}`, JSON.stringify(favorites));
      setIsFavorite(true);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.name || 'สินค้า',
          text: `ดูสินค้า ${product?.name} ใน CannaTrade`,
          url: url
        });
      } catch (error) {
        // User cancelled or error
        console.log('Share cancelled');
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        alert('คัดลอกลิงก์แล้ว!');
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    }
  };

  const fetchProduct = async () => {
    try {
      const productRef = doc(db, 'products', productId);
      const productSnap = await getDoc(productRef);
      
      if (productSnap.exists()) {
        const productData = { id: productSnap.id, ...productSnap.data() };
        setProduct(productData);
        
        // Fetch store info
        if (productData.storeId) {
          const storeRef = doc(db, 'stores', productData.storeId);
          const storeSnap = await getDoc(storeRef);
          if (storeSnap.exists()) {
            const storeData = { id: storeSnap.id, ...storeSnap.data() };
            setStore(storeData);
            
            // Setup chat for product inquiry
            if (currentUser && currentUser.uid !== storeData.ownerId) {
              try {
                const chat = await findOrCreateChat(
                  chatModel.types.PRODUCT_INQUIRY,
                  storeData.ownerId,
                  null,
                  productId,
                  productData.storeId
                );
                setChatId(chat);
              } catch (error) {
                console.error('Error setting up chat:', error);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    if (!product || !currentUser) {
      setCanReview(false);
      return;
    }

    // Check if user has delivered orders with this product
    try {
      // This is a simplified check - in production, you'd check actual orders
      // For now, we'll allow any logged-in user to review
      setCanReview(true);
    } catch (error) {
      console.error('Error checking can review:', error);
      setCanReview(false);
    }
  };

  const handleAddToCart = async () => {
    if (!currentUser) {
      alert('กรุณาเข้าสู่ระบบ');
      navigate('/login');
      return;
    }

    try {
      await addToCart(product);
      alert(t('marketplace.addToCartSuccess'));
    } catch (error) {
      alert(error.message || 'เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleReviewSuccess = () => {
    setShowReviewForm(false);
    fetchProduct(); // Reload to show new review
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">ไม่พบสินค้า</p>
          <button
            onClick={() => navigate('/marketplace')}
            className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            กลับไปหน้าหลัก
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header with Back, Favorite, and Share */}
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/marketplace')}
            className="text-green-600 hover:text-green-700 flex items-center gap-2"
          >
            ← กลับ
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleFavorite}
              className={`p-2 rounded-full transition-colors ${
                isFavorite 
                  ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
              title={isFavorite ? 'ลบออกจากรายการโปรด' : 'เพิ่มในรายการโปรด'}
            >
              {isFavorite ? '❤️' : '🤍'}
            </button>
            <button
              onClick={handleShare}
              className="p-2 rounded-full text-gray-500 hover:bg-gray-100 transition-colors"
              title="แชร์สินค้า"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 p-4 md:p-8">
            {/* Product Images */}
            <div>
              {product.images && product.images.length > 0 ? (
                <ProductImageGallery images={product.images} productName={product.name} />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center relative overflow-hidden">
                  <img 
                    src="/logo.png" 
                    alt="CannaTrade Logo" 
                    className="w-32 h-32 md:w-48 md:h-48 object-contain opacity-80"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="text-white text-6xl">🌿</div>';
                    }}
                  />
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
                {product.name}
              </h1>

              {/* Rating Display */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`text-xl ${
                        i < Math.floor(reviewStats.averageRating)
                          ? 'text-yellow-400'
                          : i < reviewStats.averageRating
                          ? 'text-yellow-300'
                          : 'text-gray-300'
                      }`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="text-lg font-semibold text-gray-700">
                  {reviewStats.averageRating > 0 ? reviewStats.averageRating.toFixed(1) : '0.0'}
                </span>
                {reviewStats.totalReviews > 0 && (
                  <span className="text-sm text-gray-500">
                    ({reviewStats.totalReviews})
                  </span>
                )}
              </div>

              {/* Category and Type */}
              <div className="flex items-center gap-2 mb-3 md:mb-4 flex-wrap">
                {product.category && (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                    {product.category}
                  </span>
                )}
                {product.type && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                    {product.type}
                  </span>
                )}
                {product.strain && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                    {product.strain}
                  </span>
                )}
              </div>

              {/* Price */}
              <div className="mb-4 md:mb-6">
                <p className="text-3xl md:text-4xl font-bold text-green-600 mb-2">
                  ฿{product.price?.toLocaleString()}
                </p>
                <div className="space-y-0.5 md:space-y-1 text-sm md:text-base">
                  <p className="text-gray-600">
                    {t('marketplace.totalQuantity')}: {product.quantity} {product.unit}
                  </p>
                  <p className={`font-semibold ${remainingQuantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {t('marketplace.remaining')}: {remainingQuantity} {product.unit}
                  </p>
                  {product.minimumOrderQuantity && (
                    <p className="text-xs md:text-sm text-gray-500">
                      {t('marketplace.minimumOrder')}: {product.minimumOrderQuantity} {product.unit}
                    </p>
                  )}
                </div>
              </div>

              {/* Edit Button for Owner */}
              {canEdit && (
                <div className="mb-4">
                  <button
                    onClick={() => navigate(`/marketplace/store?tab=products&edit=${productId}`)}
                    className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    ✏️ {t('marketplace.editProduct')}
                  </button>
                </div>
              )}

              {/* Guarantees */}
              {guarantees && guarantees.length > 0 && (
                <div className="mb-4 md:mb-6">
                  {guarantees.map((guarantee, index) => (
                    <GuaranteeBadge key={index} guarantee={guarantee} />
                  ))}
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div className="mb-4 md:mb-6">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">{t('marketplace.description')}</h3>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed line-clamp-3">{product.description}</p>
                </div>
              )}

              {/* Store Info - Enhanced with Level, Verification, and Link */}
              {store && (
                <Link 
                  to={`/marketplace/stores/${store.id}`}
                  className="mb-4 md:mb-6 p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-green-400 hover:bg-green-50 transition-all cursor-pointer block"
                >
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs md:text-sm text-gray-600">{t('marketplace.store')}</p>
                    <span className="text-xs md:text-sm text-green-600 font-semibold flex items-center gap-1">
                      ดูร้านค้า →
                    </span>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm md:text-base mb-2">{store.name}</p>
                  
                  {/* Store Level and Verification Status */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {/* Level Badge */}
                    {store.reputationLevel && (
                      <LevelBadge 
                        level={store.reputationLevel} 
                        score={store.reputationScore} 
                        type="store" 
                      />
                    )}
                    
                    {/* Verification Badge */}
                    {store.verification ? (
                      <VerificationBadge verification={store.verification} size="sm" />
                    ) : (
                      <VerificationBadge verification={null} size="sm" />
                    )}
                    
                    {/* Risk Score Badge */}
                    {store.verification?.riskScore !== undefined && (
                      <RiskScoreBadge riskScore={store.verification.riskScore} size="sm" />
                    )}
                  </div>
                  
                  {/* Store Stats */}
                  <div className="flex items-center gap-3 text-xs md:text-sm text-gray-600 mt-2">
                    {store.completedOrders > 0 && (
                      <span>
                        <span className="font-semibold">{store.completedOrders}</span> ออเดอร์
                      </span>
                    )}
                    {store.averageRating > 0 && (
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">{store.averageRating.toFixed(1)}</span>
                        <span className="text-yellow-500">★</span>
                      </span>
                    )}
                  </div>
                  
                  {store.contact?.address && (
                    <p className="text-xs md:text-sm text-gray-600 mt-2 line-clamp-1">{store.contact.address}</p>
                  )}
                </Link>
              )}

              {/* ข้อมูลการชำระเงินและการจัดส่ง - Collapsible */}
              {store && (store.payment || (store.shipping && store.shipping.methods && store.shipping.methods.length > 0)) && (
                <div className="mb-4 md:mb-6">
                  <button
                    onClick={() => setShowPaymentShipping(!showPaymentShipping)}
                    className="w-full p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center justify-between hover:bg-blue-100 transition-colors"
                  >
                    <div className="flex items-center gap-2 md:gap-3">
                      <span className="text-xl md:text-2xl">💳</span>
                      <div className="text-left">
                        <span className="font-semibold text-gray-800 text-sm md:text-base block">
                          วิธีการชำระเงินและการจัดส่ง
                        </span>
                        <span className="text-xs text-gray-600 mt-0.5 block">
                          ดูรายละเอียดการโอนเงินและค่าจัดส่ง
                        </span>
                      </div>
                    </div>
                    <svg 
                      className={`w-5 h-5 text-gray-600 transition-transform ${showPaymentShipping ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {showPaymentShipping && (
                    <div className="mt-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  
                  {/* ข้อมูลการชำระเงิน */}
                  {store.payment && (
                    <PaymentMethodsDisplay 
                      payment={store.payment} 
                      productPrice={product.price}
                    />
                  )}

                  {/* ข้อมูลการจัดส่ง */}
                  {store.shipping && store.shipping.methods && store.shipping.methods.length > 0 && (
                    <div className="border-t border-blue-200 pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">🚚 {t('marketplace.shippingMethod')}:</h4>
                      <div className="text-sm text-gray-600 space-y-3">
                        {store.shipping.methods.includes('courier') && (
                          <div className="bg-white p-3 rounded border border-gray-200">
                            <div className="font-medium mb-1">📦 {t('marketplace.privateCourier')}</div>
                            {store.shipping.courierCompanies && store.shipping.courierCompanies.length > 0 ? (
                              <div className="ml-2 text-gray-700">{store.shipping.courierCompanies.join(', ')}</div>
                            ) : (
                              <div className="ml-2 text-gray-500">{t('marketplace.courierNotSelected')}</div>
                            )}
                            {store.shipping.fees?.courier && (
                              <div className="ml-2 mt-1 text-xs text-gray-500">
                                {t('marketplace.shippingFee')}: {store.shipping.fees.courier.type === 'fixed' 
                                  ? `฿${parseFloat(store.shipping.fees.courier.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                  : 'ตามน้ำหนัก/ระยะทาง'}
                              </div>
                            )}
                          </div>
                        )}
                        {store.shipping.methods.includes('pickup') && (
                          <div className="bg-white p-3 rounded border border-gray-200">
                            <div className="font-medium mb-1 text-green-600">📍 นัดรับ (ฟรี)</div>
                            {store.shipping.pickupLocation && (
                              <div className="ml-2 text-gray-700">ที่อยู่: {store.shipping.pickupLocation}</div>
                            )}
                            {store.shipping.pickupTime && (
                              <div className="ml-2 text-gray-700">เวลา: {store.shipping.pickupTime}</div>
                            )}
                          </div>
                        )}
                        {store.shipping.methods.includes('special_vehicle') && (
                          <div className="bg-white p-3 rounded border border-gray-200">
                            <div className="font-medium mb-1">🚛 ฝากส่งกับรถพิเศษ</div>
                            {store.shipping.specialVehicleInfo && (
                              <div className="ml-2 text-gray-700">{store.shipping.specialVehicleInfo}</div>
                            )}
                            {store.shipping.fees?.specialVehicle && (
                              <div className="ml-2 mt-1 text-xs text-gray-500">
                                ค่าจัดส่ง: {store.shipping.fees.specialVehicle.type === 'fixed' 
                                  ? `฿${parseFloat(store.shipping.fees.specialVehicle.amount).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                                  : 'ตามน้ำหนัก/ระยะทาง'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional Product Info */}
              <div className="mb-4 md:mb-6 p-3 md:p-4 bg-gray-50 rounded-lg">
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2 md:mb-3">ข้อมูลเพิ่มเติม</h3>
                <div className="grid grid-cols-2 gap-3 md:gap-4 text-xs md:text-sm">
                  {product.strain && (
                    <div>
                      <p className="text-gray-600 mb-1">สายพันธุ์</p>
                      <p className="font-semibold text-gray-800">{product.strain}</p>
                    </div>
                  )}
                  {product.type && (
                    <div>
                      <p className="text-gray-600 mb-1">ประเภท</p>
                      <p className="font-semibold text-gray-800">{product.type}</p>
                    </div>
                  )}
                  {product.thc && (
                    <div>
                      <p className="text-gray-600 mb-1">THC</p>
                      <p className="font-semibold text-gray-800">{product.thc}%</p>
                    </div>
                  )}
                  {product.cbd && (
                    <div>
                      <p className="text-gray-600 mb-1">CBD</p>
                      <p className="font-semibold text-gray-800">{product.cbd}%</p>
                    </div>
                  )}
                  {product.harvestDate && (
                    <div>
                      <p className="text-gray-600 mb-1">วันที่เก็บเกี่ยว</p>
                      <p className="font-semibold text-gray-800">
                        {new Date(product.harvestDate.toDate?.() || product.harvestDate).toLocaleDateString('th-TH')}
                      </p>
                    </div>
                  )}
                  {product.expiryDate && (
                    <div>
                      <p className="text-gray-600 mb-1">วันหมดอายุ</p>
                      <p className="font-semibold text-gray-800">
                        {new Date(product.expiryDate.toDate?.() || product.expiryDate).toLocaleDateString('th-TH')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                {product.status === 'active' && store && currentUser && currentUser.uid !== store.ownerId && (
                  <button
                    onClick={handleAddToCart}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg transition-colors text-sm md:text-base"
                  >
                    🛒 {t('marketplace.addToCart')}
                  </button>
                )}
                {product.status === 'active' && store && currentUser && currentUser.uid === store.ownerId && (
                  <div className="w-full bg-gray-100 text-gray-600 font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg text-center text-sm md:text-base">
                    {t('marketplace.yourProduct')}
                  </div>
                )}
                {product.status === 'sold_out' && (
                  <div className="w-full bg-red-100 text-red-800 font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg text-center text-sm md:text-base">
                    {t('marketplace.outOfStock')}
                  </div>
                )}
                {product.status === 'auction' && (
                  <button
                    onClick={() => navigate(`/marketplace/auctions/${product.auctionId}`)}
                    className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2.5 md:py-3 px-4 md:px-6 rounded-lg transition-colors text-sm md:text-base"
                  >
                    🔨 {t('marketplace.viewAuction')}
                  </button>
                )}
                
                {/* Contact Actions - จัดกลุ่มปุ่มแชทและ Video Call */}
                {store && currentUser && currentUser.uid !== store.ownerId && (
                  <div className="grid grid-cols-2 gap-2">
                    {/* Chat Button */}
                    <button
                      onClick={async () => {
                        if (!chatId && store) {
                          try {
                            const chat = await findOrCreateChat(
                              chatModel.types.PRODUCT_INQUIRY,
                              store.ownerId,
                              null,
                              productId,
                              product.storeId
                            );
                            setChatId(chat);
                            setShowChat(true);
                          } catch (error) {
                            console.error('Error creating chat:', error);
                            alert('เกิดข้อผิดพลาดในการสร้างแชท');
                          }
                        } else {
                          setShowChat(!showChat);
                        }
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2.5 md:py-3 px-3 md:px-4 rounded-lg transition-colors text-sm md:text-base flex items-center justify-center gap-1.5"
                    >
                      <span>💬</span>
                      <span className="hidden sm:inline">{showChat ? 'ซ่อนแชท' : 'สอบถาม'}</span>
                      <span className="sm:hidden">แชท</span>
                    </button>
                    
                    {/* Video Call Button - รวม Status และ Usage */}
                    <div className="relative">
                      <button
                        onClick={async () => {
                          const { isVideoCallAvailableNow } = await import('../../utils/videoCallUtils');
                          const result = await isVideoCallAvailableNow(store.id, productId);
                          
                          if (result.available) {
                            if (productId && product?.storeId) {
                              navigate(`/marketplace/video-call/product/${productId}/store/${product.storeId}`);
                            } else if (chatId) {
                              navigate(`/marketplace/video-call/${chatId}`);
                            }
                          } else {
                            setShowRequestVideoCallModal(true);
                          }
                        }}
                        className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2.5 md:py-3 px-3 md:px-4 rounded-lg transition-colors text-sm md:text-base flex items-center justify-center gap-1.5"
                      >
                        <VideoCallIcon className="w-4 h-4 md:w-5 md:h-5" />
                        <span className="hidden sm:inline">Video Call</span>
                        <span className="sm:hidden">Call</span>
                      </button>
                      {/* Usage Badge - แสดงเล็กๆ ที่มุม (ซ่อนบนมือถือ) */}
                      <div className="absolute -top-1 -right-1 hidden md:block">
                        <VideoCallUsageBadge size="sm" />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Video Call Status - แสดงแบบ compact ใต้ปุ่ม */}
                {store && currentUser && currentUser.uid !== store.ownerId && (
                  <div className="pt-1">
                    <VideoCallStatusBadge storeId={store.id} productId={productId} />
                  </div>
                )}
                
                {/* Request Video Call - แสดงเฉพาะเมื่อไม่ available now */}
                {store && currentUser && currentUser.uid !== store.ownerId && (
                  <button
                    onClick={() => setShowRequestVideoCallModal(true)}
                    className="w-full bg-yellow-50 hover:bg-yellow-100 text-yellow-800 border border-yellow-300 font-medium py-2 md:py-2.5 px-4 rounded-lg transition-colors text-sm md:text-base flex items-center justify-center gap-2"
                  >
                    <span>📅</span>
                    <span>จอง Video Call</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Chat Section */}
          {showChat && chatId && store && (
            <div className="border-t p-4 md:p-8">
              <div className="mb-3 md:mb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 md:mb-2">สอบถามสินค้า</h2>
                <p className="text-sm md:text-base text-gray-600">แชทกับร้าน {store.name}</p>
              </div>
              
              {/* Quick Chat Questions */}
              <QuickChatQuestions
                product={product}
                store={store}
                onQuestionClick={async (question) => {
                  // Send question as message via ChatContext
                  if (chatId && sendMessage) {
                    try {
                      await sendMessage(chatId, question);
                    } catch (error) {
                      console.error('Error sending quick question:', error);
                      alert('เกิดข้อผิดพลาดในการส่งข้อความ');
                    }
                  }
                }}
              />
              
              <div className="h-96 flex flex-col">
                <ChatWindow
                  chatId={chatId}
                  otherUserId={store.ownerId}
                  otherUserName={store.name}
                  productInfo={product}
                  onClose={() => setShowChat(false)}
                />
              </div>
            </div>
          )}

          {/* Reviews Section */}
          <div className="border-t p-4 md:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 md:mb-6">
              <div className="flex items-center gap-2 md:gap-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('marketplace.productReviews')}</h2>
                <Link
                  to={`/marketplace/reviews/product/${productId}`}
                  className="text-xs md:text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {t('marketplace.viewAllReviews')}
                </Link>
              </div>
              {canReview && currentUser && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="px-3 md:px-4 py-1.5 md:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm md:text-base"
                >
                  + {t('marketplace.writeReview')}
                </button>
              )}
            </div>

            {showReviewForm && (
              <div className="mb-6">
                <ProductReviewForm
                  product={product}
                  orderId={orderId}
                  onClose={() => setShowReviewForm(false)}
                  onSuccess={handleReviewSuccess}
                />
              </div>
            )}

            <ProductReviewList productId={productId} showStats={true} />
          </div>
        </div>
      </div>

      {/* Request Video Call Modal */}
      {showRequestVideoCallModal && store && product && (
        <RequestVideoCallModal
          isOpen={showRequestVideoCallModal}
          onClose={() => setShowRequestVideoCallModal(false)}
          product={product}
          store={store}
        />
      )}
    </div>
  );
};

export default ProductDetail;

